import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { TrendingUp, TrendingDown, Lock, Clock, ChevronDown, ChevronUp, Star, Target, Shield, AlertTriangle, Cpu, Info } from 'lucide-react';
import { formatPrice, getTP1, getTP3, getTargetPrice, getStopLoss, normalizeAdvice } from '../shared/formatters';
import { formatTime } from '../shared/formatters';

// v9.0: Professional Trading UI - Soft Colors & Progress Bar

// Soft color palette - Premium trading platform style
const COLORS = {
  green: {
    bg: 'bg-emerald-900/20',
    border: 'border-emerald-600/30',
    text: 'text-emerald-400',
    softText: 'text-emerald-300',
    progress: 'bg-emerald-500',
  },
  red: {
    bg: 'bg-rose-900/20',
    border: 'border-rose-600/30',
    text: 'text-rose-400',
    softText: 'text-rose-300',
    progress: 'bg-rose-500',
  },
  amber: {
    bg: 'bg-amber-900/20',
    border: 'border-amber-600/30',
    text: 'text-amber-400',
    softText: 'text-amber-300',
  },
  slate: {
    bg: 'bg-slate-800/40',
    border: 'border-slate-600/30',
    text: 'text-slate-400',
  }
};

// Signal status calculator
const getSignalStatus = (signal, currentPrice, entryPrice, stopLoss, tp1, tp2, tp3) => {
  const pnl = signal.pnl_percent;
  const isLong = signal.action === 'BUY';
  
  // TP hit statuses
  if (signal.tp3_hit) return { type: 'tp3', label: 'TP3 Ulaştı', icon: '🏆', color: 'purple' };
  if (signal.tp2_hit) return { type: 'tp2', label: 'TP2 Ulaştı', icon: '🎯', color: 'blue' };
  if (signal.tp1_hit) return { type: 'tp1', label: 'TP1 Ulaştı', icon: '✓', color: 'green' };
  
  // Outcome based statuses
  if (signal.outcome_status === 'stopped' || signal.sl_hit) {
    return { type: 'stopped', label: 'Stop Vurdu', icon: '✕', color: 'red' };
  }
  
  // Calculate distance to stop for warning
  if (currentPrice && entryPrice && stopLoss) {
    const distanceToStop = isLong 
      ? ((currentPrice - stopLoss) / (entryPrice - stopLoss)) * 100
      : ((stopLoss - currentPrice) / (stopLoss - entryPrice)) * 100;
    
    if (distanceToStop < 30) {
      return { type: 'warning', label: 'Dikkat', icon: '⚠', color: 'amber' };
    }
  }
  
  // Check if profitable
  if (pnl !== undefined && pnl !== null) {
    if (pnl > 0) return { type: 'profit', label: 'Kârlı', icon: '●', color: 'green' };
    if (pnl < -1) return { type: 'loss', label: 'Zararda', icon: '●', color: 'amber' };
  }
  
  return { type: 'active', label: 'Aktif', icon: '●', color: 'green' };
};

// Progress bar calculator - Risk-Reward based
const calculateProgress = (signal, currentPrice, entryPrice, stopLoss, tp2) => {
  if (!currentPrice || !entryPrice || !stopLoss || !tp2) return null;
  
  const isLong = signal.action === 'BUY';
  const totalRange = Math.abs(tp2 - stopLoss);
  
  let progress;
  if (isLong) {
    progress = ((currentPrice - stopLoss) / totalRange) * 100;
  } else {
    progress = ((stopLoss - currentPrice) / totalRange) * 100;
  }
  
  // Clamp between 0 and 100
  return Math.max(0, Math.min(100, progress));
};

// Sinyal Gücü hesaplama
const getSignalStrength = (signal) => {
  const score = signal.sniper_score || signal.quality_score || 0;
  let grade = signal.sniper_grade || signal.signal_grade || '';
  
  if (!grade && score > 0) {
    if (score >= 80) grade = 'A+';
    else if (score >= 70) grade = 'A';
    else if (score >= 60) grade = 'B';
    else grade = 'C';
  }
  
  const gradeDisplay = {
    'A+': { label: 'Mükemmel', stars: 5, color: 'text-amber-400' },
    'A': { label: 'Çok Güçlü', stars: 4, color: 'text-amber-400' },
    'B': { label: 'Güçlü', stars: 3, color: 'text-amber-500' },
    'C': { label: 'İyi', stars: 2, color: 'text-slate-400' }
  };
  
  return gradeDisplay[grade] || gradeDisplay['C'];
};

// Star rating component - With "Güç" label, tooltip and glow effect
const StarRating = ({ stars, className = '', glowEnabled = false }) => (
  <span 
    className={`inline-flex items-center gap-1 cursor-default ${className}`}
    title={`Sinyal Gücü: ${stars}/5`}
  >
    <span className="inline-flex" style={glowEnabled && stars >= 3 ? { filter: 'drop-shadow(0 0 3px rgba(251, 191, 36, 0.5))' } : {}}>
      {[...Array(5)].map((_, i) => (
        <Star 
          key={i} 
          className={`h-3 w-3 ${i < stars ? 'fill-amber-400 text-amber-400' : 'fill-transparent text-slate-600'}`} 
        />
      ))}
    </span>
  </span>
);

// Status Badge Component - Flat, no animation
const StatusBadge = ({ status }) => {
  const colorMap = {
    green: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    amber: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    red: 'bg-rose-500/15 text-rose-400 border-rose-500/20',
    blue: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
    purple: 'bg-purple-500/15 text-purple-400 border-purple-500/20',
  };
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${colorMap[status.color]}`}>
      <span>{status.icon}</span>
      <span>{status.label}</span>
    </span>
  );
};

// Progress Bar Component - Single color, professional
const ProgressBar = ({ progress, isProfit, showLabel = true, labelText = '' }) => {
  const barColor = isProfit ? 'bg-emerald-500' : 'bg-amber-500';
  
  return (
    <div className="w-full">
      {showLabel && (
        <div className="flex justify-between text-[10px] text-slate-500 mb-1">
          <span>Stop</span>
          <span className={isProfit ? 'text-emerald-400' : 'text-amber-400'}>{labelText}</span>
          <span>Hedef</span>
        </div>
      )}
      <div className="h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
        <div 
          className={`h-full ${barColor} rounded-full transition-all duration-500`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

// TP Level Component - Horizontal
const TPLevels = ({ tp1, tp2, tp3, tp1Hit, tp2Hit, tp3Hit }) => {
  const getLevelStyle = (hit) => hit 
    ? 'text-emerald-400' 
    : 'text-slate-500';
  
  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="flex items-center gap-1">
        <span className={getLevelStyle(tp1Hit)}>
          {tp1Hit ? '✓' : '○'} TP1
        </span>
        <span className="font-mono text-slate-300">{formatPrice(tp1)}</span>
      </div>
      <span className="text-slate-600">|</span>
      <div className="flex items-center gap-1">
        <span className={getLevelStyle(tp2Hit)}>
          {tp2Hit ? '✓' : '◎'} TP2
        </span>
        <span className="font-mono text-slate-300">{formatPrice(tp2)}</span>
      </div>
      {tp3 && (
        <>
          <span className="text-slate-600">|</span>
          <div className="flex items-center gap-1">
            <span className={getLevelStyle(tp3Hit)}>
              {tp3Hit ? '✓' : '○'} TP3
            </span>
            <span className="font-mono text-slate-300">{formatPrice(tp3)}</span>
          </div>
        </>
      )}
    </div>
  );
};

// AI Analysis Expand Component - v10.0 Premium Design
const AIAnalysisPanel = ({ signal, isExpanded, onToggle, isPending = false }) => {
  // Get AI commentary data from signal - v10.5: normalizeAdvice ile emir kiplerini dönüştür
  const rawCommentaryOriginal = signal.commentary || signal.ai_commentary || signal.sebep;
  const rawCommentary = normalizeAdvice(rawCommentaryOriginal);
  const riskLevel = signal.risk_level || signal.ai_risk_level || 'ORTA';
  const riskNotes = signal.risk_notes || signal.ai_risk_notes || [];
  
  // v10.4: Premium language - ÖZET (kapalı) vs DETAY (açık) ayrımı
  // Varyant kelimeler: korunuyor, geçerli, devam ediyor, sürüyor
  const generateSummary = () => {
    const isLong = signal.action === 'BUY';
    const score = signal.sniper_score || signal.quality_score || 0;
    const symbol = signal.symbol || '';
    
    // Coin bazlı varyasyon için basit hash
    const symbolHash = symbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 3;
    
    if (isPending) {
      if (score >= 70) {
        if (isLong) {
          const variants = ['Yukarı yönlü momentum korunuyor', 'Alıcı baskısı belirgin', 'Pozitif yapı devam ediyor'];
          return variants[symbolHash];
        } else {
          const variants = ['Aşağı yönlü yapı korunuyor', 'Satış baskısı devam ediyor', 'Negatif momentum sürdürülüyor'];
          return variants[symbolHash];
        }
      } else if (score >= 50) {
        if (isLong) {
          const variants = ['Trend yönü korunuyor', 'Yukarı yönlü eğilim', 'Alıcı ilgisi devam ediyor'];
          return variants[symbolHash];
        } else {
          const variants = ['Aşağı yönlü baskı korunuyor', 'Düşüş eğilimi devam ediyor', 'Zayıf görünüm sürdürülüyor'];
          return variants[symbolHash];
        }
      } else {
        const variants = ['İzleme modunda', 'Belirsiz bölge', 'Nötr alan'];
        return variants[symbolHash];
      }
    }
    return isLong ? 'Pozisyon yapısı geçerli' : 'SHORT senaryo devam ediyor';
  };
  
  const generateDetail = () => {
    const isLong = signal.action === 'BUY';
    const score = signal.sniper_score || signal.quality_score || 0;
    const symbol = signal.symbol || '';
    
    // Coin bazlı varyasyon için basit hash
    const symbolHash = symbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 3;
    
    if (isPending) {
      if (score >= 70) {
        if (isLong) {
          const longVariants = [
            'Yukarı yönlü momentum korunuyor.',
            'Alıcı baskısı belirgin, yapı sağlam.',
            'Pozitif trend yapısı devam ediyor.'
          ];
          return longVariants[symbolHash];
        } else {
          const shortVariants = [
            'Aşağı yönlü yapı korunuyor.',
            'Satış baskısı devam ediyor.',
            'Negatif momentum geçerliliğini sürdürüyor.'
          ];
          return shortVariants[symbolHash];
        }
      } else if (score >= 50) {
        if (isLong) {
          const longVariants = [
            'Trend yönü korunuyor, yapı bozulmadı.',
            'Yukarı yönlü eğilim devam ediyor.',
            'Alıcı ilgisi sürdürülüyor.'
          ];
          return longVariants[symbolHash];
        } else {
          const shortVariants = [
            'Aşağı yönlü baskı korunuyor.',
            'Düşüş eğilimi devam ediyor.',
            'Zayıf görünüm sürdürülüyor.'
          ];
          return shortVariants[symbolHash];
        }
      } else {
        const neutralVariants = [
          'İzleme modunda, yapı netleşmedi.',
          'Belirsiz bölge, yön teyidi bekleniyor.',
          'Nötr alan, piyasa yapısı izleniyor.'
        ];
        return neutralVariants[symbolHash];
      }
    }
    // Option a - basit dil (aktif pozisyonlar için)
    if (signal.tp2_hit) {
      return isLong 
        ? 'Ana hedefe ulaşıldı. Kârın bir kısmı güvenceye alınabilir.'
        : 'SHORT senaryo güçleniyor. Kârın bir kısmı güvenceye alınabilir.';
    }
    if (signal.tp1_hit) {
      return isLong
        ? 'İlk hedefe ulaşıldı. Stop seviyesi yükseltilebilir.'
        : 'İlk hedef alındı. Stop seviyesi yükseltilebilir.';
    }
    return isLong 
      ? 'Pozisyon takipte. Risk ve hedef seviyeleri izleniyor.'
      : 'SHORT senaryo geçerli. Risk ve hedef seviyeleri kontrol altında.';
  };
  
  // Kapalı state için özet
  const getSummaryText = () => {
    if (rawCommentary) {
      const firstSentence = rawCommentary.split('.')[0];
      return firstSentence.length > 40 ? firstSentence.substring(0, 40) + '...' : firstSentence;
    }
    return generateSummary();
  };
  
  const commentary = rawCommentary || generateDetail();
  const summaryText = getSummaryText();
  
  // Determine prefix based on state
  const getPrefix = () => {
    if (isPending) return 'İzleme';
    if (signal.tp3_hit) return 'Güncelleme';
    if (signal.tp2_hit || signal.tp1_hit) return 'Güncelleme';
    return 'Durum';
  };
  const prefix = getPrefix();
  
  // Risk level colors
  const riskColors = {
    'DÜŞÜK': 'text-emerald-400 bg-emerald-500/10',
    'LOW': 'text-emerald-400 bg-emerald-500/10',
    'ORTA': 'text-amber-400 bg-amber-500/10',
    'MEDIUM': 'text-amber-400 bg-amber-500/10',
    'YÜKSEK': 'text-rose-400 bg-rose-500/10',
    'HIGH': 'text-rose-400 bg-rose-500/10'
  };
  
  const riskLabels = {
    'LOW': 'DÜŞÜK',
    'MEDIUM': 'ORTA',
    'HIGH': 'YÜKSEK',
    'DÜŞÜK': 'DÜŞÜK',
    'ORTA': 'ORTA',
    'YÜKSEK': 'YÜKSEK'
  };
  
  // Truncate commentary for collapsed view (max 55 chars) - artık kullanılmıyor
  // const shortCommentary = commentary.length > 55 ? commentary.substring(0, 55) + '...' : commentary;
  
  return (
    <div className="mt-2 pt-2 border-t border-slate-700/20">
      {/* Collapsed View - Özet satır (tekrar yok) */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-xs hover:bg-slate-800/30 rounded px-2 py-1.5 transition-colors group"
      >
        <div className="flex items-center gap-2 text-slate-400 min-w-0 flex-1">
          <Cpu className="h-3 w-3 text-blue-400 flex-shrink-0" />
          <span className="text-blue-400/80 font-medium flex-shrink-0">{prefix} ·</span>
          <span className="text-slate-300 truncate">{summaryText}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          <span className="text-slate-500 text-[10px] group-hover:text-slate-300">Detay</span>
          {isExpanded ? (
            <ChevronUp className="h-3 w-3 text-slate-500 group-hover:text-slate-300" />
          ) : (
            <ChevronDown className="h-3 w-3 text-slate-500 group-hover:text-slate-300" />
          )}
        </div>
      </button>
      
      {/* Expanded View - Full panel (detay) */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-2 p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
              {/* Başlık */}
              <div className="flex items-center gap-2 mb-1.5">
                <Cpu className="h-3.5 w-3.5 text-blue-400" />
                <span className="text-[10px] text-blue-400/80 font-medium uppercase tracking-wide">Sistem Analizi</span>
              </div>
              
              {/* Detay metni - tırnak yok, terminal style */}
              <p className="text-sm text-slate-300 leading-relaxed mb-3 pl-5">
                {commentary}
              </p>
              
              {/* Risk Level - Only show if AI commentary exists */}
              {rawCommentary && (
                <div className="flex items-center gap-3 pl-5 mb-2">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wide">Risk ·</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${riskColors[riskLevel] || riskColors['ORTA']}`}>
                    {riskLevel === 'LOW' || riskLevel === 'DÜŞÜK' ? '🟢' : riskLevel === 'HIGH' || riskLevel === 'YÜKSEK' ? '🔴' : '🟡'} {riskLabels[riskLevel] || 'ORTA'}
                  </span>
                </div>
              )}
              
              {/* Risk Notes - Max 2, "Notlar" başlığı ile */}
              {riskNotes && riskNotes.length > 0 && (
                <div className="pl-5">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wide">Notlar</span>
                  <div className="space-y-1 mt-1">
                    {riskNotes.slice(0, 2).map((note, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-[11px] text-slate-400">
                        <span className="text-slate-600">•</span>
                        <span>{note}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SignalCard = ({ signal, isBlurred = false, isFeatured = false, livePrices = {}, marketStatus = null }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAIAnalysis, setShowAIAnalysis] = useState(false);
  
  const entryPrice = signal.entry_price || signal.giris_fiyati || signal.price;
  const tp1 = getTP1(signal);
  const tp2 = getTargetPrice(signal);
  const tp3 = getTP3(signal);
  const stopLoss = getStopLoss(signal);
  const isPending = (signal.status || '').toLowerCase() === 'pending' || signal.outcome_status === 'pending';
  const isLong = signal.action === 'BUY';
  
  // Check if signal is expired
  const isExpired = () => {
    const now = new Date();
    if (signal.expires_at) {
      try {
        const expiresAt = new Date(signal.expires_at);
        if (!isNaN(expiresAt.getTime()) && expiresAt < now) return true;
      } catch (e) {}
    }
    const dateStr = signal.timestamp || signal.created_at || signal.analysis_time;
    if (dateStr) {
      try {
        const signalTime = new Date(dateStr);
        if (!isNaN(signalTime.getTime())) {
          const expirationTime = new Date(signalTime.getTime() + 8 * 60 * 60 * 1000);
          if (expirationTime < now) return true;
        }
      } catch (e) {}
    }
    return false;
  };
  
  if (isExpired()) return null;
  
  // Get live current price
  const getLivePrice = () => {
    if (livePrices && livePrices[signal.symbol]) return livePrices[signal.symbol];
    if (marketStatus) {
      const allSymbols = [...(marketStatus.symbols || []), ...(marketStatus.position_symbols || [])];
      const symbolData = allSymbols.find(s => s.symbol === signal.symbol);
      if (symbolData?.indicators?.price) return symbolData.indicators.price;
      if (symbolData?.position_info?.guncel_fiyat) return symbolData.position_info.guncel_fiyat;
    }
    return signal.current_price_at_signal || signal.current_price;
  };
  const currentPrice = getLivePrice();
  
  // Calculate PnL
  const pnl = signal.pnl_percent;
  const isProfit = pnl !== undefined && pnl !== null && pnl >= 0;
  
  // Calculate locked profit (if TP1 hit)
  const lockedProfit = signal.tp1_hit && signal.tp1_pnl ? signal.tp1_pnl : null;
  
  // Get status
  const status = getSignalStatus(signal, currentPrice, entryPrice, stopLoss, tp1, tp2, tp3);
  
  // Calculate progress
  const progress = calculateProgress(signal, currentPrice, entryPrice, stopLoss, tp2);
  const progressLabel = progress !== null ? `%${Math.round(progress)} ilerleme` : '';
  
  // Get signal strength
  const strength = getSignalStrength(signal);
  
  // Time remaining for pending
  const getTimeRemaining = () => {
    if (signal.expires_at) {
      try {
        const expirationTime = new Date(signal.expires_at);
        if (!isNaN(expirationTime.getTime())) {
          const remaining = expirationTime - new Date();
          if (remaining <= 0) return null;
          const hours = Math.floor(remaining / (1000 * 60 * 60));
          const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
          return `${hours}s ${minutes}dk`;
        }
      } catch (e) {}
    }
    const dateStr = signal.timestamp || signal.created_at || signal.analysis_time;
    if (!dateStr) return "8s 0dk";
    try {
      const signalTime = new Date(dateStr);
      if (isNaN(signalTime.getTime())) return "8s 0dk";
      const expirationTime = new Date(signalTime.getTime() + 8 * 60 * 60 * 1000);
      const remaining = expirationTime - new Date();
      if (remaining <= 0) return null;
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      return `${hours}s ${minutes}dk`;
    } catch (e) {
      return "8s 0dk";
    }
  };
  
  // ===== PENDING CARD - v10.0 Enhanced UX with Entry-Current Relationship =====
  if (isPending && !isBlurred) {
    const timeRemaining = getTimeRemaining();
    
    // Calculate distance to entry (percentage)
    const distanceToEntry = currentPrice && entryPrice 
      ? isLong 
        ? ((currentPrice - entryPrice) / entryPrice) * 100
        : ((entryPrice - currentPrice) / entryPrice) * 100
      : null;
    const isCloseToEntry = distanceToEntry !== null && Math.abs(distanceToEntry) < 1;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* NEUTRAL BACKGROUND - Subtle hover effect */}
        <Card className="border border-slate-600/40 bg-slate-800/30 overflow-hidden hover:border-slate-500/60 hover:bg-slate-800/40 transition-all duration-150">
          <CardContent className="p-3 md:p-4">
            {/* Row 1: Header - Status + Symbol + Direction + Stars + Time */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {/* Status Badge - with glow pulse animation */}
                <span 
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition-all ${
                    isCloseToEntry 
                      ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' 
                      : 'bg-slate-700/40 text-slate-300 border-slate-600/30'
                  }`}
                  style={isCloseToEntry ? {
                    animation: 'pulse-glow 2s ease-in-out infinite',
                    boxShadow: '0 0 10px rgba(16, 185, 129, 0.4)'
                  } : {
                    animation: 'subtle-shimmer 4s ease-in-out infinite'
                  }}
                >
                  <Clock className="h-3 w-3" />
                  <span>{isCloseToEntry ? 'Giriş Yakın' : 'Bekliyor'}</span>
                </span>
                {/* Symbol */}
                <span className="font-bold text-white text-base md:text-lg">{signal.symbol}</span>
                {/* Direction Badge */}
                <Badge className={`text-xs px-1.5 py-0.5 font-medium ${isLong ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                  {isLong ? 'LONG' : 'SHORT'}
                </Badge>
                {/* Stars with glow */}
                <StarRating stars={strength.stars} glowEnabled={true} />
              </div>
              {/* Time Remaining */}
              <span className="text-xs text-amber-400 font-mono">
                Süre: {timeRemaining || 'Doldu'}
              </span>
            </div>
            
            {/* Row 2: Giriş | Mesafe | Güncel Fiyat (with meaningful metric in center) */}
            <div className="flex items-center justify-between mb-3 py-3 px-4 bg-slate-900/50 rounded-lg border border-slate-700/30">
              {/* Giriş Fiyatı */}
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Giriş</p>
                <p className="text-xl font-bold text-white font-mono">{formatPrice(entryPrice)}</p>
              </div>
              
              {/* Girişe Mesafe - The Key Metric */}
              <div className="flex flex-col items-center px-4">
                <p className="text-[9px] text-slate-500 uppercase tracking-wide mb-1">Girişe Mesafe</p>
                <p className={`text-lg font-bold font-mono ${
                  distanceToEntry !== null && Math.abs(distanceToEntry) < 0.5 
                    ? 'text-emerald-400' 
                    : distanceToEntry !== null && Math.abs(distanceToEntry) < 1.5
                      ? 'text-amber-400'
                      : 'text-slate-300'
                }`}>
                  {distanceToEntry !== null ? `%${Math.abs(distanceToEntry).toFixed(2)}` : '-'}
                </p>
              </div>
              
              {/* Güncel Fiyat - slightly brighter with shimmer */}
              <div className="text-right">
                <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Güncel Fiyat</p>
                <p 
                  className="text-xl font-bold text-slate-100 font-mono"
                  style={{ animation: 'subtle-shimmer 3s ease-in-out infinite' }}
                >
                  {currentPrice ? formatPrice(currentPrice) : '-'}
                </p>
              </div>
            </div>
            
            {/* Row 3: TP1 (emphasized) / TP2 / TP3 (muted) & STOP (softer) */}
            <div className="grid grid-cols-4 gap-2 mt-1">
              {/* TP1 - Emphasized (first target is most important for pending) */}
              <div className="py-2.5 px-3 bg-emerald-900/15 rounded-lg border border-emerald-600/25 text-center hover:border-emerald-500/40 hover:bg-emerald-900/25 transition-all duration-150 cursor-default">
                <p className="text-xs text-emerald-400/80 mb-0.5">◎ TP1</p>
                <p className="text-sm font-semibold font-mono text-emerald-400">{formatPrice(tp1)}</p>
              </div>
              {/* TP2 - Muted */}
              <div className="py-2.5 px-3 bg-slate-800/40 rounded-lg border border-slate-700/30 text-center hover:border-slate-600/40 transition-all duration-150 cursor-default opacity-70">
                <p className="text-xs text-slate-500 mb-0.5">○ TP2</p>
                <p className="text-sm font-medium font-mono text-slate-400">{formatPrice(tp2)}</p>
              </div>
              {/* TP3 - More Muted */}
              {tp3 ? (
                <div className="py-2.5 px-3 bg-slate-800/30 rounded-lg border border-slate-700/20 text-center hover:border-slate-600/30 transition-all duration-150 cursor-default opacity-50">
                  <p className="text-xs text-slate-600 mb-0.5">○ TP3</p>
                  <p className="text-sm font-medium font-mono text-slate-500">{formatPrice(tp3)}</p>
                </div>
              ) : (
                <div></div>
              )}
              {/* STOP - Softer/Secondary for pending state */}
              <div className="py-2.5 px-3 bg-slate-800/50 rounded-lg border border-amber-600/20 text-center hover:border-amber-500/30 transition-all duration-150 cursor-default">
                <p className="text-xs text-amber-500/70 mb-0.5">🛡️ Stop</p>
                <p className="text-sm font-medium font-mono text-amber-500/80">{formatPrice(stopLoss)}</p>
              </div>
            </div>
            
            {/* Row 4: AI Analysis - Expandable (for pending cards too) */}
            <AIAnalysisPanel 
              signal={signal} 
              isExpanded={showAIAnalysis} 
              onToggle={() => setShowAIAnalysis(!showAIAnalysis)}
              isPending={true}
            />
          </CardContent>
        </Card>
      </motion.div>
    );
  }
  
  // ===== BLURRED CARD =====
  if (isBlurred) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Card className={`border relative overflow-hidden ${isLong ? 'border-emerald-600/20 bg-emerald-900/10' : 'border-rose-600/20 bg-rose-900/10'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="font-bold text-white text-lg">{signal.symbol}</span>
                <Badge className={isLong ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}>
                  {isLong ? 'LONG' : 'SHORT'}
                </Badge>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {['Giriş', 'Stop', 'TP1', 'TP2'].map((label) => (
                <div key={label} className="p-2 bg-slate-800/40 rounded text-center">
                  <p className="text-[10px] text-slate-500">{label}</p>
                  <p className="font-mono text-sm text-white blur-[6px]">$0,000</p>
                </div>
              ))}
            </div>
            <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-[2px] flex items-center justify-center">
              <div className="text-center">
                <Lock className="h-5 w-5 text-slate-500 mx-auto mb-1" />
                <p className="text-xs text-slate-400">Detayları görmek için kayıt ol</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }
  
  // ===== ACTIVE CARD - NEW PROFESSIONAL DESIGN =====
  const colors = isLong ? COLORS.green : COLORS.red;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      data-testid={`signal-card-${signal.id || signal._id}`}
    >
      <Card className={`border ${colors.border} ${colors.bg} overflow-hidden hover:border-slate-500/50 transition-colors`}>
        <CardContent className="p-3 md:p-4">
          {/* Row 1: Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {/* Status Badge */}
              <StatusBadge status={status} />
              {/* Symbol */}
              <span className="font-bold text-white text-base md:text-lg">{signal.symbol}</span>
              {/* Direction Badge */}
              <Badge className={`text-[10px] px-1.5 py-0.5 font-medium ${isLong ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                {isLong ? 'LONG' : 'SHORT'}
              </Badge>
              {/* Stars - Desktop only */}
              <span className="hidden md:inline-flex">
                <StarRating stars={strength.stars} />
              </span>
            </div>
            {/* PnL Display */}
            <div className="text-right">
              {pnl !== undefined && pnl !== null ? (
                <span className={`text-xl md:text-2xl font-bold font-mono ${isProfit ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {isProfit ? '+' : ''}{pnl.toFixed(2)}%
                </span>
              ) : (
                <span className="text-lg text-slate-500">-</span>
              )}
            </div>
          </div>
          
          {/* Row 2: Progress Bar */}
          {progress !== null && (
            <div className="mb-3">
              <ProgressBar 
                progress={progress} 
                isProfit={isProfit} 
                labelText={progressLabel}
              />
            </div>
          )}
          
          {/* Row 3: TP Levels + Locked Profit */}
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <TPLevels 
              tp1={tp1} 
              tp2={tp2} 
              tp3={tp3}
              tp1Hit={signal.tp1_hit}
              tp2Hit={signal.tp2_hit}
              tp3Hit={signal.tp3_hit}
            />
            {/* Locked Profit - Inline */}
            {lockedProfit && (
              <span className="text-[10px] text-amber-400">
                <Lock className="h-3 w-3 inline mr-0.5" />
                Kilitli: <span className="font-mono font-medium">+{lockedProfit.toFixed(1)}%</span>
              </span>
            )}
          </div>
          
          {/* Row 4: Giriş → Güncel → Anlık K/Z (Kompakt Tek Satır - Beyaz) */}
          <div className="flex items-center justify-between text-xs pt-2 border-t border-slate-700/30">
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Giriş</span>
              <span className="font-mono text-slate-300">{formatPrice(entryPrice)}</span>
              <span className="text-slate-600">→</span>
              <span className="text-slate-500">Güncel</span>
              <span className="font-mono text-slate-300">{currentPrice ? formatPrice(currentPrice) : '-'}</span>
              <span className="text-slate-600">→</span>
              <span className="text-slate-500">Anlık K/Z</span>
              <span className="font-mono text-slate-300">
                {pnl >= 0 ? '+' : ''}{pnl?.toFixed(2) || '0.00'}%
              </span>
            </div>
            <span className="text-amber-400/80">
              <Shield className="h-3 w-3 inline mr-0.5" />
              Stop: <span className="font-mono">{formatPrice(stopLoss)}</span>
            </span>
          </div>
          
          {/* Row 5: Time */}
          <div className="flex items-center justify-between text-[10px] text-slate-500 mt-2">
            <span>{formatTime(signal.timestamp || signal.created_at)}</span>
            {signal.confidence > 0 && (
              <span className="text-slate-400">Güven: %{Math.round(signal.confidence * 100)}</span>
            )}
          </div>
          
          {/* Row 6: AI Analysis - Expandable */}
          <AIAnalysisPanel 
            signal={signal} 
            isExpanded={showAIAnalysis} 
            onToggle={() => setShowAIAnalysis(!showAIAnalysis)} 
          />
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SignalCard;
